import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import Page from '../../components/shared/Page/Page';
import StyledContentLayout from '../../styles/StyledContentLayout';
import useAuth from '../../hooks/useAuth';
import { useDashboardAgents } from '../../hooks/queries/useDashboardAgents';
import { useDashboardChatAgents } from '../../hooks/queries/useDashboardChatAgents';
import { useSubscription } from '../../contexes/SubscriptionProvider';
import { fetchUsage, UsageResponse } from '../../services/billingService';
import { useTranslation } from '../../hooks/useTranslation';
import {
  enrichUsageWithCompany,
  enterpriseCreditsConsumptionPercent,
  creditsCapForDisplay,
  creditsUsedForDisplay,
  formatEquivMinutes,
  formatEquivMessages,
  resolvedEquivMinutesRemaining,
  resolvedEquivMessagesRemaining,
  resolvedPoolRemaining,
  showCreditPoolSection,
} from '../../helpers/creditUsageDisplay';
import { usageAmount } from '../../helpers/usageAmount';
import { isEnterpriseSubscriber } from '../../helpers/billingPlanSwitchUi';
import { resolveUsageLimitsFromPlan } from '../../helpers/planUsageLimits';
import { getKnowledgesByUserId } from '../../services/knowledgeService';
import Knowledge from '../../interfaces/Knowledge';
import UsageCallChatMeterPapers from './UsageCallChatMeterPapers';
import OverageUsageMeter from '../../components/billing/OverageUsageMeter';
import { shouldShowOverageUsageMeter } from '../../helpers/overageLimitDisplay';

const usagePaperSx = {
  p: { xs: 2.5, sm: 3 },
  width: '100%',
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
};

const Usage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { subscription, company } = useSubscription();
  const isEnterprisePlan = isEnterpriseSubscriber(company, subscription);
  const { data: callAgents = [] } = useDashboardAgents(
    !!currentUser && !isEnterprisePlan
  );
  const { data: chatAgents = [] } = useDashboardChatAgents(
    !!currentUser && !isEnterprisePlan
  );
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [knowledgesUsed, setKnowledgesUsed] = useState(0);
  const [loadingKnowledges, setLoadingKnowledges] = useState(true);

  useEffect(() => {
    if (!currentUser?.company_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchUsage(currentUser.company_id as string).then(data => {
      if (!cancelled) {
        setUsage(data ?? null);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.company_id]);

  useEffect(() => {
    if (!currentUser?.id || isEnterprisePlan) {
      setKnowledgesUsed(0);
      setLoadingKnowledges(false);
      return;
    }
    let cancelled = false;
    setLoadingKnowledges(true);
    getKnowledgesByUserId(currentUser.id)
      .then(result => {
        if (cancelled) return;
        const items = ((result as { message?: unknown })?.message ??
          []) as Knowledge[];
        setKnowledgesUsed(Array.isArray(items) ? items.length : 0);
      })
      .catch(() => {
        if (!cancelled) {
          setKnowledgesUsed(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingKnowledges(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, isEnterprisePlan]);

  const dataUnavailable =
    !loading && (!currentUser?.company_id || usage === null);

  const usageData = useMemo(
    () => enrichUsageWithCompany(usage, company),
    [usage, company]
  );

  const hasRealData = !loading && !dataUnavailable && usageData !== null;

  const creditPoolMode =
    hasRealData && usageData ? showCreditPoolSection(usageData) : false;

  const isFreePlan =
    hasRealData &&
    !!usageData &&
    ((usageData.plan_tier ?? company?.plan_tier ?? '').toLowerCase() ===
      'free' ||
      (usageData.plan_id ?? company?.plan_id ?? '')
        .toLowerCase()
        .includes('_free'));

  const hideSplitCallChatBars =
    (hasRealData && usageData?.bundle_credit_pool === true) || isFreePlan;

  const minutesUsed = usageData ? usageAmount(usageData.minutes_used) : 0;
  const messagesUsed = usageData ? usageAmount(usageData.messages_used) : 0;
  const { minutesLimit, messagesLimit } = resolveUsageLimitsFromPlan(
    usageData?.plan_id ?? company?.plan_id,
    usageData?.minutes_limit ?? null,
    usageData?.messages_limit ?? null
  );

  const hasMinuteCap = minutesLimit !== null;
  const hasMessageCap = messagesLimit !== null;

  const minutesPercent =
    hasMinuteCap && (minutesLimit ?? 0) > 0
      ? Math.min(100, (minutesUsed / (minutesLimit ?? 1)) * 100)
      : 0;
  const messagesPercent =
    hasMessageCap && (messagesLimit ?? 0) > 0
      ? Math.min(100, (messagesUsed / (messagesLimit ?? 1)) * 100)
      : 0;

  const minutesOverage =
    hasRealData && usageData && hasMinuteCap
      ? Math.max(0, minutesUsed - (minutesLimit ?? 0))
      : 0;
  const messagesOverage =
    hasRealData && usageData && hasMessageCap
      ? Math.max(0, messagesUsed - (messagesLimit ?? 0))
      : 0;

  const minutesBarColor = minutesOverage
    ? 'error'
    : minutesPercent >= 90
      ? 'error'
      : minutesPercent >= 70
        ? 'warning'
        : 'primary';

  const messagesBarColor = messagesOverage
    ? 'error'
    : messagesPercent >= 90
      ? 'error'
      : messagesPercent >= 70
        ? 'warning'
        : 'primary';

  const agentsUsed = callAgents.length + chatAgents.length;
  const agentsLimit = usageData?.max_agents ?? null;
  const hasAgentsCap = agentsLimit !== null;
  const agentsPercent =
    hasAgentsCap && agentsLimit > 0
      ? Math.min(100, (agentsUsed / agentsLimit) * 100)
      : 0;
  const agentsOverage =
    hasRealData && hasAgentsCap ? Math.max(0, agentsUsed - agentsLimit) : 0;
  const agentsBarColor = agentsOverage
    ? 'error'
    : agentsPercent >= 90
      ? 'error'
      : agentsPercent >= 70
        ? 'warning'
        : 'primary';
  const agentsUsageLabel = loading
    ? null
    : hasAgentsCap
      ? `${agentsUsed}/${agentsLimit ?? 0}`
      : `${agentsUsed}/0`;

  const knowledgesLimit = usageData?.max_knowledges ?? null;
  const hasKnowledgesCap = knowledgesLimit !== null;
  const knowledgesPercent =
    hasKnowledgesCap && knowledgesLimit > 0
      ? Math.min(100, (knowledgesUsed / knowledgesLimit) * 100)
      : 0;
  const knowledgesOverage =
    hasRealData && hasKnowledgesCap
      ? Math.max(0, knowledgesUsed - knowledgesLimit)
      : 0;
  const knowledgesBarColor = knowledgesOverage
    ? 'error'
    : knowledgesPercent >= 90
      ? 'error'
      : knowledgesPercent >= 70
        ? 'warning'
        : 'primary';
  const knowledgesUsageLabel =
    loading || loadingKnowledges
      ? null
      : hasKnowledgesCap
        ? `${knowledgesUsed}/${knowledgesLimit ?? 0}`
        : `${knowledgesUsed}/0`;

  const creditCap = usageData ? creditsCapForDisplay(usageData) : null;
  const hasCreditsCap = creditCap != null && creditCap > 0;
  const creditsUsed = usageData ? creditsUsedForDisplay(usageData) : 0;
  const creditsRemaining =
    usageData != null
      ? Math.round((resolvedPoolRemaining(usageData) ?? 0) * 10) / 10
      : 0;
  const creditPercent =
    hasRealData && creditPoolMode && usageData && hasCreditsCap
      ? enterpriseCreditsConsumptionPercent(usageData)
      : 0;

  const minutesUsageLabel = loading
    ? null
    : hasMinuteCap
      ? `${minutesUsed}/${minutesLimit ?? 0}`
      : `${minutesUsed}/0`;

  const messagesUsageLabel = loading
    ? null
    : hasMessageCap
      ? `${messagesUsed}/${messagesLimit ?? 0}`
      : `${messagesUsed}/0`;

  const hideAgentsAndKnowledges =
    isEnterprisePlan || usageData?.enterprise === true;

  const showPlanRow = Boolean(
    subscription?.product_name ||
      usageData?.enterprise ||
      usageData?.plan_tier ||
      usageData?.plan_type ||
      usageData?.plan_id ||
      company?.enterprise ||
      company?.plan_tier ||
      company?.plan_type ||
      company?.plan_id
  );

  return (
    <Page title={t('Usage')}>
      <StyledContentLayout wide>
        <Box
          sx={{
            width: '100%',
            p: { xs: 1.25, md: 2 },
            bgcolor: '#FFFFFF',
            borderRadius: { xs: 3, md: 4 },
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
          }}
        >
          <h1>{t('Monthly usage')}</h1>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 880, lineHeight: 1.65 }}
          >
            {t('Consumption this billing period vs your plan limits.')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {hasRealData && creditPoolMode && usageData ? (
              <Paper sx={usagePaperSx}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  {t('Balance')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('Shared credits hint')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={loading ? 0 : creditPercent}
                    sx={{
                      flex: 1,
                      minWidth: 160,
                      height: 10,
                      borderRadius: 1,
                      ...(!hasCreditsCap ? { opacity: 0.45 } : {}),
                    }}
                    color="primary"
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      loading
                        ? t('Loading...')
                        : hasCreditsCap
                          ? t('Credits balance label', {
                              used: creditsUsed,
                              remaining: creditsRemaining,
                            })
                          : t('Credits balance unlimited label', {
                              used: creditsUsed,
                            })
                    }
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  />
                </Box>
                {!isFreePlan ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5 }}
                      fontWeight={600}
                    >
                      {t('Call minutes remaining')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      {t('Equiv call minutes from credits', {
                        minutes: formatEquivMinutes(
                          resolvedEquivMinutesRemaining(usageData)
                        ),
                      })}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5 }}
                      fontWeight={600}
                    >
                      {t('Chat messages remaining')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: shouldShowOverageUsageMeter(usageData) ? 1.5 : 0,
                      }}
                    >
                      {t('Equiv chat messages from credits', {
                        messages: formatEquivMessages(
                          resolvedEquivMessagesRemaining(usageData)
                        ),
                      })}
                    </Typography>
                  </>
                ) : null}
                <OverageUsageMeter usage={usageData} loading={loading} />
              </Paper>
            ) : null}

            {!hideSplitCallChatBars && (
              <UsageCallChatMeterPapers
                usagePaperSx={usagePaperSx}
                loading={loading}
                dataUnavailable={dataUnavailable}
                hasRealData={hasRealData}
                hasMinuteCap={hasMinuteCap}
                hasMessageCap={hasMessageCap}
                minutesOverage={minutesOverage}
                messagesOverage={messagesOverage}
                minutesPercent={minutesPercent}
                messagesPercent={messagesPercent}
                minutesBarColor={minutesBarColor}
                messagesBarColor={messagesBarColor}
                minutesUsageLabel={minutesUsageLabel}
                messagesUsageLabel={messagesUsageLabel}
                t={t}
              />
            )}

            {hasRealData &&
            usageData &&
            !creditPoolMode &&
            shouldShowOverageUsageMeter(usageData) ? (
              <Paper sx={usagePaperSx}>
                <OverageUsageMeter usage={usageData} loading={loading} />
              </Paper>
            ) : null}
            {!hideAgentsAndKnowledges && (
              <Paper sx={{ ...usagePaperSx, order: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  {t('Agents')}
                </Typography>
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <LinearProgress
                      variant="indeterminate"
                      sx={{
                        flex: 1,
                        minWidth: 160,
                        height: 10,
                        borderRadius: 1,
                      }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('Loading...')}
                      sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                    />
                  </Box>
                ) : (
                  <Box>
                    {agentsOverage > 0 && (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="error.main"
                        sx={{ mb: 1 }}
                      >
                        {`${t('Agents')}: +${agentsOverage}`}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={hasAgentsCap ? agentsPercent : 0}
                        sx={{
                          flex: 1,
                          minWidth: 160,
                          height: 10,
                          borderRadius: 1,
                          ...(!hasAgentsCap ? { opacity: 0.45 } : {}),
                        }}
                        color={agentsBarColor}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={agentsUsageLabel ?? '—'}
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            )}
            {!hideAgentsAndKnowledges && (
              <Paper sx={{ ...usagePaperSx, order: 4 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  {t('Knowledges')}
                </Typography>
                {loading || loadingKnowledges ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <LinearProgress
                      variant="indeterminate"
                      sx={{
                        flex: 1,
                        minWidth: 160,
                        height: 10,
                        borderRadius: 1,
                      }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('Loading...')}
                      sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                    />
                  </Box>
                ) : (
                  <Box>
                    {knowledgesOverage > 0 && (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="error.main"
                        sx={{ mb: 1 }}
                      >
                        {`${t('Knowledges')}: +${knowledgesOverage}`}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={hasKnowledgesCap ? knowledgesPercent : 0}
                        sx={{
                          flex: 1,
                          minWidth: 160,
                          height: 10,
                          borderRadius: 1,
                          ...(!hasKnowledgesCap ? { opacity: 0.45 } : {}),
                        }}
                        color={knowledgesBarColor}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={knowledgesUsageLabel ?? '—'}
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('Plan')}:{' '}
              {loading ? (
                t('Loading...')
              ) : showPlanRow && usageData ? (
                <>
                  {usageData.enterprise
                    ? t('Enterprise')
                    : (usageData.plan_tier ?? '-')}
                  {!hideAgentsAndKnowledges &&
                    usageData.max_agents != null &&
                    ` · ${t('Max agents')}: ${usageData.max_agents}`}
                  {!hideAgentsAndKnowledges &&
                    usageData.max_knowledges != null &&
                    ` · ${t('Max knowledges')}: ${usageData.max_knowledges}`}
                </>
              ) : showPlanRow ? (
                <>
                  {subscription?.product_name
                    ? subscription.product_name
                    : company?.enterprise
                      ? t('Enterprise')
                      : company?.plan_tier
                        ? company.plan_type
                          ? `${company.plan_tier} · ${company.plan_type}`
                          : company.plan_tier
                        : (company?.plan_type ?? company?.plan_id ?? '—')}
                </>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
        </Box>
      </StyledContentLayout>
    </Page>
  );
};

export default Usage;
